import { default as React, useCallback, useEffect, useState } from 'react';
import { AlertColor } from '@mui/material';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  GroupType,
  GroupTypeDetails,
  SmartSuggestionSettings,
  SmartSuggestionSettingsInput
} from 'graphql/types';
import { useMessage } from 'modules/intl';
import {
  EnabledDisabled,
  SmartSuggestionSettingUseFieldName
} from './constant';
import { useGetSmartSuggestionDefaultValues } from './hooks/useGetDefaultValues';
import { usePutSmartSuggestionSettings } from './hooks/usePutSmartSuggestionSettings';
import { SettingBody } from './SettingBody';
import { mapToAccessSope } from './utils';

type SmartSuggestionPage = {
  enabledGroupTypes: GroupTypeDetails[];
  settings: SmartSuggestionSettings[];
};

export const SmartSuggestionPage: React.FC<SmartSuggestionPage> = ({
  enabledGroupTypes,
  settings
}: SmartSuggestionPage) => {
  const [show, setShow] = useState<boolean>(false);
  const category = 'success' as AlertColor;
  const message = useMessage('settings.smartSugeestionSaveSuccessMessage');

  const fieldValues = useGetSmartSuggestionDefaultValues(settings);
  const formMethods = useForm(fieldValues);
  const { control, formState, reset, getValues } = formMethods;
  const [defaultValues, setDefaultValues] = useState<any>();
  const { isDirty, isSubmitSuccessful } = formState;
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: SmartSuggestionSettingUseFieldName
  });
  const {
    putSmartSuggestionSettings,
    loading: saving
  } = usePutSmartSuggestionSettings();
  const handleClick = useCallback(() => {
    if (enabledGroupTypes && enabledGroupTypes.length) {
      const values = enabledGroupTypes.reduce((a, c) => {
        a[c.value] = [];
        return a;
      }, {} as Record<GroupType, Array<any>>);
      prepend({
        ...values,
        ...{
          enableDisabled: EnabledDisabled.enabled
        }
      });
    }
  }, [enabledGroupTypes, prepend]);
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...defaultValues });
    }
  }, [reset, defaultValues, isSubmitSuccessful]);
  useEffect(() => {
    reset(getValues());
  }, [getValues, reset]);
  const onSubmit = useCallback(
    (formData) => {
      const saveSettings = async () => {
        let settings: SmartSuggestionSettingsInput[] = [];
        if (formData[SmartSuggestionSettingUseFieldName]) {
          settings = formData[SmartSuggestionSettingUseFieldName].map(
            (r: any) => {
              return {
                enabled: r.enableDisabled === EnabledDisabled.enabled,
                accessScopeIn: {
                  ...mapToAccessSope('departmentGroups', r.DEPARTMENT),
                  ...mapToAccessSope('employeeTypeGroups', r.EMPLOYEE_TYPE),
                  ...mapToAccessSope('locations', r.LOCATION),
                  ...mapToAccessSope('costCenters', r.COST_CENTER),
                  ...mapToAccessSope('divisions', r.DIVISION),
                  ...mapToAccessSope('serviceCenters', r.SERVICE_CENTER)
                }
              };
            }
          );
        }
        settings = settings.concat({
          enabled:
            formData.defaultRow.enableDisabled === EnabledDisabled.enabled
        });
        await putSmartSuggestionSettings({ settings: settings });
        setShow(false);
        setShow(true);
      };

      saveSettings();
      setDefaultValues(formData);
    },
    [putSmartSuggestionSettings]
  );
  const handleRemove = useCallback(
    (rowId) => {
      remove(rowId);
    },
    [remove]
  );
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <SettingBody
          enabledGroupTypes={enabledGroupTypes}
          fields={fields}
          show={show}
          category={category}
          message={message}
          isDirty={isDirty}
          handleClick={handleClick}
          handleRemove={handleRemove}
          saving={saving}
          fieldName={SmartSuggestionSettingUseFieldName}
          defaultRowValue={fieldValues.defaultValues.defaultEnabled}
        />
      </form>
    </FormProvider>
  );
};
